import { useState, useEffect } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
  opacity: ${props => props.isOpen ? 1 : 0};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

const Panel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 600px;
  max-width: 100vw;
  height: 100vh;
  background: var(--bg-1);
  border-left: 1px solid var(--border);
  box-shadow: -8px 0 32px rgba(0, 0, 0, 0.5);
  z-index: 1000;
  overflow-y: auto;
  padding: 24px;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  background: var(--bg-1);
  z-index: 10;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border);
`;

const Title = styled.h2`
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-0);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: var(--text-2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: 1px solid transparent;
  font-size: 20px;
  color: var(--text-2);
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;

  &:hover {
    background: var(--bg-3);
    border-color: var(--border);
    color: var(--text-0);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-2);
  font-family: 'IBM Plex Mono', monospace;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 13px;
  color: var(--text-0);
  background: var(--bg-2);

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const TextArea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 13px;
  color: var(--text-0);
  background: var(--bg-2);
  min-height: 80px;
  resize: vertical;
  font-family: 'IBM Plex Sans', sans-serif;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const ArrayInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ArrayItem = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const RemoveButton = styled.button`
  padding: 6px 10px;
  background: var(--red-dim);
  color: var(--red);
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: var(--red);
  }
`;

const AddButton = styled.button`
  padding: 7px 14px;
  background: var(--bg-3);
  color: var(--text-1);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 4px;

  &:hover {
    border-color: var(--accent);
    color: var(--accent);
  }
`;

const SaveButton = styled.button`
  padding: 10px 20px;
  background: var(--accent-dim);
  color: var(--accent);
  border: 1px solid var(--accent);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 16px;
  font-family: 'IBM Plex Mono', monospace;
  letter-spacing: 0.5px;

  &:hover {
    background: var(--accent);
    color: var(--bg-0);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  padding: 12px;
  border-radius: 6px;
  font-size: 14px;
  background: ${props => props.type === 'success' ? '#c6f6d5' : '#fed7d7'};
  color: ${props => props.type === 'success' ? '#22543d' : '#c53030'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: var(--text-2);
  font-size: 13px;
`;

export default function ProfileEditor({ isOpen, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchProfile();
    }
  }, [isOpen]);

  const fetchProfile = async () => {
    setLoading(true);
    const token = localStorage.getItem('bd_auth_token');

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL ?? ''}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile);
        if (data.profile.websiteUrl) setWebsiteUrl(data.profile.websiteUrl);
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    setMessage(null);

    const token = localStorage.getItem('bd_auth_token');

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL ?? ''}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(profile)
      });

      if (res.ok) {
        setMessage({ type: 'success', text: 'Profile saved successfully!' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save profile' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Connection error' });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!websiteUrl.trim()) {
      setMessage({ type: 'error', text: 'Enter a website URL first' });
      return;
    }
    setGenerating(true);
    setMessage(null);
    const token = localStorage.getItem('bd_auth_token');

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL ?? ''}/api/profile/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ websiteUrl: websiteUrl.trim() })
      });

      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile);
        setMessage({ type: 'success', text: `Profile generated from ${websiteUrl} — review and save.` });
      } else {
        const err = await res.json().catch(() => ({}));
        setMessage({ type: 'error', text: err.error || 'Failed to generate profile' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Connection error during profile generation' });
    } finally {
      setGenerating(false);
    }
  };

  const updateField = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const addArrayItem = (field) => {
    setProfile(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeArrayItem = (field, index) => {
    setProfile(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Object-array helpers for the Company Truth Model (Chunk 2). Each bucket
  // holds an array of {key: value} objects instead of an array of strings, so
  // we can render structured fields per entry.
  const addObjectArrayItem = (field, template) => {
    setProfile(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), { ...template }]
    }));
  };
  const updateObjectArrayItem = (field, index, key, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: (prev[field] || []).map((item, i) => i === index ? { ...item, [key]: value } : item)
    }));
  };
  const removeObjectArrayItem = (field, index) => {
    setProfile(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index)
    }));
  };

  if (loading || !profile) {
    return (
      <>
        <Overlay isOpen={isOpen} onClick={onClose} />
        <Panel isOpen={isOpen}>
          <CloseButton onClick={onClose}>×</CloseButton>
          <EmptyState>Loading profile...</EmptyState>
        </Panel>
      </>
    );
  }

  return (
    <>
      <Overlay isOpen={isOpen} onClick={onClose} />
      <Panel isOpen={isOpen}>
        <CloseButton onClick={onClose}>×</CloseButton>

      <Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title>Company Profile</Title>
            <Subtitle>This information helps the AI score opportunities for your company</Subtitle>
          </div>
          <SaveButton
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{ marginTop: 0, flexShrink: 0, marginLeft: 16 }}
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </SaveButton>
        </div>
        {message && (
          <Message type={message.type} style={{ marginTop: 12 }}>{message.text}</Message>
        )}
      </Header>

      <Form onSubmit={handleSave}>
        <FormSection style={{ padding: '16px', background: 'var(--bg-2)', borderRadius: '8px', border: '1px solid var(--border)' }}>
          <Label>Auto-Generate from Website</Label>
          <Subtitle style={{ marginBottom: 8 }}>Enter your company website and AI will build your profile automatically</Subtitle>
          <div style={{ display: 'flex', gap: 8 }}>
            <Input
              type="url"
              value={websiteUrl || profile.websiteUrl || ''}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://www.yourcompany.com"
              style={{ flex: 1 }}
            />
            <SaveButton
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              style={{ marginTop: 0, whiteSpace: 'nowrap' }}
            >
              {generating ? 'Generating...' : 'Generate Profile'}
            </SaveButton>
          </div>
          {generating && <Subtitle style={{ color: 'var(--accent)', marginTop: 4 }}>Scraping website and analyzing with AI... this takes 15-30 seconds</Subtitle>}
        </FormSection>

        <FormSection>
          <Label>Company Name</Label>
          <Input
            type="text"
            value={profile.companyName}
            onChange={(e) => updateField('companyName', e.target.value)}
            placeholder="Your company name"
          />
        </FormSection>

        <FormSection>
          <Label>Company Description</Label>
          <TextArea
            value={profile.companyDescription}
            onChange={(e) => updateField('companyDescription', e.target.value)}
            placeholder="Brief description of your company and what you do"
          />
        </FormSection>

        <FormSection>
          <Label>
            Capability Paper
            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-2)', marginLeft: 8 }}>
              — used by BD Team agents as the authoritative source for every draft
            </span>
          </Label>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 8, lineHeight: 1.55 }}>
            Long-form narrative (markdown supported). Include team bios, methodology, past-performance stories with metrics, quality processes, security posture, pricing posture, teaming history.
            The BD Team writer will pull from this <strong>verbatim where it fits the SOW</strong>, so the richer this is, the closer every drafted proposal lands to submission-ready. Aim for 800-2500 words.
          </div>
          <TextArea
            value={profile.capabilityPaper || ''}
            onChange={(e) => updateField('capabilityPaper', e.target.value)}
            placeholder={`## About ${profile.companyName || '[Company]'}\n\n[1-2 paragraphs on what we do and why we win]\n\n## Team & Key Personnel\n\n[Bios of PM, technical leads, key staff]\n\n## Past Performance\n\n### [Customer / Program]\n- Contract value, period of performance\n- Scope: ...\n- Outcomes: [specific metrics]\n- Reference: [name, role]\n\n## Methodology\n\n[How we run programs — cadence, tools, risk management]\n\n## Quality & Compliance\n\n[CMMI, ISO, CMMC level, certifications]\n\n## Differentiators\n\n[3-5 specific things only we do well, with proof points]`}
            style={{ minHeight: 320, fontFamily: 'var(--font-mono)', fontSize: 12, lineHeight: 1.55 }}
          />
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
            {(profile.capabilityPaper || '').length.toLocaleString()} chars ·
            {' '}{(profile.capabilityPaper || '').trim().split(/\s+/).filter(Boolean).length.toLocaleString()} words
          </div>
        </FormSection>

        {/* ── COMPANY TRUTH MODEL (Chunk 2 — three explicitly separated buckets) ── */}
        <FormSection style={{ borderTop: '2px solid var(--accent)', paddingTop: 20 }}>
          <Label style={{ fontSize: 14, color: 'var(--accent)' }}>
            Company Truth Model
            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-2)', marginLeft: 8 }}>
              — structured record the BD Team uses to keep corporate PP vs personnel experience distinct
            </span>
          </Label>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 14, lineHeight: 1.55 }}>
            Three buckets. Never conflated. This is what keeps proposals from claiming founder combat/instructor experience as
            <strong> corporate</strong> past performance — a common credibility failure the BD Team's review flagged.
          </div>
        </FormSection>

        {/* Bucket 1: Corporate Past Performance */}
        <FormSection>
          <Label>
            Bucket 1: Corporate Past Performance
            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-2)', marginLeft: 8 }}>
              — contracts {profile.companyName || 'the company'} itself performed on (prime or sub). Includes contract number, PoP, value, outcomes.
            </span>
          </Label>
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 10 }}>
            Only entries here may be cited as <strong>corporate</strong> past performance in a drafted proposal. If {profile.companyName || 'the company'} didn't hold the contract, it doesn't belong in Bucket 1.
          </div>
          {(profile.corporatePastPerformance || []).map((c, i) => (
            <div key={i} style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', minWidth: 24 }}>#{i + 1}</span>
                <Input
                  type="text"
                  value={c.name || ''}
                  onChange={(e) => updateObjectArrayItem('corporatePastPerformance', i, 'name', e.target.value)}
                  placeholder="Short contract / program name (e.g. Offensive Cyber C2)"
                  style={{ flex: 1 }}
                />
                <RemoveButton onClick={() => removeObjectArrayItem('corporatePastPerformance', i)}>Remove</RemoveButton>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Input type="text" value={c.customer || ''} onChange={(e) => updateObjectArrayItem('corporatePastPerformance', i, 'customer', e.target.value)} placeholder="Customer (e.g. USAF, ONR)" />
                <Input type="text" value={c.contractNumber || ''} onChange={(e) => updateObjectArrayItem('corporatePastPerformance', i, 'contractNumber', e.target.value)} placeholder="Contract # (or 'unknown')" />
                <Input type="text" value={c.role || ''} onChange={(e) => updateObjectArrayItem('corporatePastPerformance', i, 'role', e.target.value)} placeholder="Role (Prime / Sub)" />
                <Input type="text" value={c.periodOfPerformance || ''} onChange={(e) => updateObjectArrayItem('corporatePastPerformance', i, 'periodOfPerformance', e.target.value)} placeholder="PoP (e.g. 2023-2026)" />
                <Input type="text" value={c.value || ''} onChange={(e) => updateObjectArrayItem('corporatePastPerformance', i, 'value', e.target.value)} placeholder="Value (e.g. $5M)" />
                <Input type="text" value={c.reference || ''} onChange={(e) => updateObjectArrayItem('corporatePastPerformance', i, 'reference', e.target.value)} placeholder="Reference (name, role — optional)" />
              </div>
              <TextArea
                value={c.scope || ''}
                onChange={(e) => updateObjectArrayItem('corporatePastPerformance', i, 'scope', e.target.value)}
                placeholder={`Scope: 1-2 sentences on what ${profile.companyName || 'the company'} delivered`}
                style={{ marginTop: 8, minHeight: 60, fontSize: 12 }}
              />
              <TextArea
                value={c.outcomes || ''}
                onChange={(e) => updateObjectArrayItem('corporatePastPerformance', i, 'outcomes', e.target.value)}
                placeholder="Outcomes: specific quantified results (e.g. reduced eval time 6wk→5d)"
                style={{ marginTop: 8, minHeight: 60, fontSize: 12 }}
              />
            </div>
          ))}
          <AddButton type="button" onClick={() => addObjectArrayItem('corporatePastPerformance', { name: '', customer: '', contractNumber: '', role: '', periodOfPerformance: '', value: '', scope: '', outcomes: '', reference: '' })}>
            + Add Corporate Contract
          </AddButton>
        </FormSection>

        {/* Bucket 2: Key Personnel Experience */}
        <FormSection>
          <Label>
            Bucket 2: Key Personnel Experience
            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-2)', marginLeft: 8 }}>
              — individuals and their prior experience (often at other employers or in uniform)
            </span>
          </Label>
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 10 }}>
            Prior-employer or in-service experience of individuals. This may be cited as <strong>key personnel qualifications</strong> in proposals — NEVER as company corporate past performance.
          </div>
          {(profile.keyPersonnel || []).map((p, i) => (
            <div key={i} style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', minWidth: 24 }}>#{i + 1}</span>
                <Input
                  type="text"
                  value={p.name || ''}
                  onChange={(e) => updateObjectArrayItem('keyPersonnel', i, 'name', e.target.value)}
                  placeholder="Full name"
                  style={{ flex: 1 }}
                />
                <RemoveButton onClick={() => removeObjectArrayItem('keyPersonnel', i)}>Remove</RemoveButton>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <Input type="text" value={p.currentRoleAtCompany || ''} onChange={(e) => updateObjectArrayItem('keyPersonnel', i, 'currentRoleAtCompany', e.target.value)} placeholder={`Current role at ${profile.companyName || 'company'} (e.g. Program Manager)`} />
                <Input type="text" value={p.relationship || ''} onChange={(e) => updateObjectArrayItem('keyPersonnel', i, 'relationship', e.target.value)} placeholder="Relationship (Employee / Advisor / Recruitable)" />
                <Input type="text" value={p.clearance || ''} onChange={(e) => updateObjectArrayItem('keyPersonnel', i, 'clearance', e.target.value)} placeholder="Clearance (e.g. TS/SCI)" />
                <Input type="text" value={p.yearsExperience || ''} onChange={(e) => updateObjectArrayItem('keyPersonnel', i, 'yearsExperience', e.target.value)} placeholder="Years experience (e.g. 20+)" />
              </div>
              <Input
                type="text"
                value={p.specialties || ''}
                onChange={(e) => updateObjectArrayItem('keyPersonnel', i, 'specialties', e.target.value)}
                placeholder="Specialties (comma-separated: AIC, TOPGUN, GBAD, etc.)"
                style={{ marginTop: 8 }}
              />
              <TextArea
                value={p.priorExperience || ''}
                onChange={(e) => updateObjectArrayItem('keyPersonnel', i, 'priorExperience', e.target.value)}
                placeholder="Prior experience: 2-4 lines on specific past roles/programs/employers/uniformed service. Attribute clearly (this is not company PP)."
                style={{ marginTop: 8, minHeight: 70, fontSize: 12 }}
              />
            </div>
          ))}
          <AddButton type="button" onClick={() => addObjectArrayItem('keyPersonnel', { name: '', currentRoleAtCompany: '', relationship: '', clearance: '', yearsExperience: '', specialties: '', priorExperience: '' })}>
            + Add Key Person
          </AddButton>
        </FormSection>

        {/* Bucket 3: Transferable Capabilities */}
        <FormSection>
          <Label>
            Bucket 3: Transferable Capabilities
            <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-2)', marginLeft: 8 }}>
              — analytical inferences about underlying competencies. Each needs a proof point in Bucket 1 or 2.
            </span>
          </Label>
          <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 10 }}>
            Example: "operator workflow analysis" — proof point: "Cyber C2 (USAF), Bucket 1 #1" — applicable to: "training design, mission-thread analysis, HMI eval." Downstream, these carry [ASSESSMENT] tier evidence weight, not [CONFIRMED].
          </div>
          {(profile.transferableCapabilities || []).map((t, i) => (
            <div key={i} style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', minWidth: 24 }}>#{i + 1}</span>
                <Input
                  type="text"
                  value={t.competency || ''}
                  onChange={(e) => updateObjectArrayItem('transferableCapabilities', i, 'competency', e.target.value)}
                  placeholder="Underlying competency (e.g. Mission-thread analysis)"
                  style={{ flex: 1 }}
                />
                <RemoveButton onClick={() => removeObjectArrayItem('transferableCapabilities', i)}>Remove</RemoveButton>
              </div>
              <Input
                type="text"
                value={t.proofPoint || ''}
                onChange={(e) => updateObjectArrayItem('transferableCapabilities', i, 'proofPoint', e.target.value)}
                placeholder="Proof point (which Bucket 1 or Bucket 2 entry proves this?)"
              />
              <Input
                type="text"
                value={Array.isArray(t.applicableTo) ? t.applicableTo.join(', ') : (t.applicableTo || '')}
                onChange={(e) => updateObjectArrayItem('transferableCapabilities', i, 'applicableTo', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                placeholder="Applicable to (comma-separated adjacent domains)"
                style={{ marginTop: 8 }}
              />
            </div>
          ))}
          <AddButton type="button" onClick={() => addObjectArrayItem('transferableCapabilities', { competency: '', proofPoint: '', applicableTo: [] })}>
            + Add Transferable Capability
          </AddButton>
        </FormSection>

        <FormSection>
          <Label>Core Capabilities</Label>
          <ArrayInput>
            {profile.coreCapabilities.map((item, i) => (
              <ArrayItem key={i}>
                <Input
                  type="text"
                  value={item}
                  onChange={(e) => updateArrayItem('coreCapabilities', i, e.target.value)}
                  placeholder="e.g., Mission Command Interfaces"
                  style={{ flex: 1 }}
                />
                <RemoveButton onClick={() => removeArrayItem('coreCapabilities', i)}>
                  Remove
                </RemoveButton>
              </ArrayItem>
            ))}
            <AddButton type="button" onClick={() => addArrayItem('coreCapabilities')}>
              + Add Capability
            </AddButton>
          </ArrayInput>
        </FormSection>

        <FormSection>
          <Label>Past Performance</Label>
          <ArrayInput>
            {profile.pastPerformance.map((item, i) => (
              <ArrayItem key={i}>
                <Input
                  type="text"
                  value={item}
                  onChange={(e) => updateArrayItem('pastPerformance', i, e.target.value)}
                  placeholder="e.g., Navy MV-20 USV Interfaces"
                  style={{ flex: 1 }}
                />
                <RemoveButton onClick={() => removeArrayItem('pastPerformance', i)}>
                  Remove
                </RemoveButton>
              </ArrayItem>
            ))}
            <AddButton type="button" onClick={() => addArrayItem('pastPerformance')}>
              + Add Past Performance
            </AddButton>
          </ArrayInput>
        </FormSection>

        <FormSection>
          <Label>NAICS Codes</Label>
          <ArrayInput>
            {profile.naicsCodes.map((item, i) => (
              <ArrayItem key={i}>
                <Input
                  type="text"
                  value={item}
                  onChange={(e) => updateArrayItem('naicsCodes', i, e.target.value)}
                  placeholder="e.g., 541511"
                  style={{ flex: 1 }}
                />
                <RemoveButton onClick={() => removeArrayItem('naicsCodes', i)}>
                  Remove
                </RemoveButton>
              </ArrayItem>
            ))}
            <AddButton type="button" onClick={() => addArrayItem('naicsCodes')}>
              + Add NAICS Code
            </AddButton>
          </ArrayInput>
        </FormSection>

        <FormSection>
          <Label>Target Agencies</Label>
          <ArrayInput>
            {profile.targetAgencies.map((item, i) => (
              <ArrayItem key={i}>
                <Input
                  type="text"
                  value={item}
                  onChange={(e) => updateArrayItem('targetAgencies', i, e.target.value)}
                  placeholder="e.g., U.S. Air Force"
                  style={{ flex: 1 }}
                />
                <RemoveButton onClick={() => removeArrayItem('targetAgencies', i)}>
                  Remove
                </RemoveButton>
              </ArrayItem>
            ))}
            <AddButton type="button" onClick={() => addArrayItem('targetAgencies')}>
              + Add Agency
            </AddButton>
          </ArrayInput>
        </FormSection>

        <FormSection>
          <Label>Technical Expertise</Label>
          <ArrayInput>
            {profile.technicalExpertise.map((item, i) => (
              <ArrayItem key={i}>
                <Input
                  type="text"
                  value={item}
                  onChange={(e) => updateArrayItem('technicalExpertise', i, e.target.value)}
                  placeholder="e.g., React / TypeScript"
                  style={{ flex: 1 }}
                />
                <RemoveButton onClick={() => removeArrayItem('technicalExpertise', i)}>
                  Remove
                </RemoveButton>
              </ArrayItem>
            ))}
            <AddButton type="button" onClick={() => addArrayItem('technicalExpertise')}>
              + Add Expertise
            </AddButton>
          </ArrayInput>
        </FormSection>

        <FormSection>
          <Label>Certifications</Label>
          <ArrayInput>
            {profile.certifications.map((item, i) => (
              <ArrayItem key={i}>
                <Input
                  type="text"
                  value={item}
                  onChange={(e) => updateArrayItem('certifications', i, e.target.value)}
                  placeholder="e.g., CMMI Level 3"
                  style={{ flex: 1 }}
                />
                <RemoveButton onClick={() => removeArrayItem('certifications', i)}>
                  Remove
                </RemoveButton>
              </ArrayItem>
            ))}
            <AddButton type="button" onClick={() => addArrayItem('certifications')}>
              + Add Certification
            </AddButton>
          </ArrayInput>
        </FormSection>

        <FormSection>
          <Label>Team Size</Label>
          <Input
            type="text"
            value={profile.teamSize}
            onChange={(e) => updateField('teamSize', e.target.value)}
            placeholder="e.g., 25-50"
          />
        </FormSection>

        <FormSection>
          <Label>Years in Business</Label>
          <Input
            type="text"
            value={profile.yearsInBusiness}
            onChange={(e) => updateField('yearsInBusiness', e.target.value)}
            placeholder="e.g., 25+"
          />
        </FormSection>

        <FormSection>
          <Label>Personnel / legacy security clearance (not company FCL)</Label>
          <Input
            type="text"
            value={profile.securityClearance}
            onChange={(e) => updateField('securityClearance', e.target.value)}
            placeholder="e.g., Secret"
          />
        </FormSection>

        <FormSection>
          <Label>Company Facility Clearance (FCL)</Label>
          <Input as="select" value={profile.facilityClearance || 'unknown'} onChange={e => updateField('facilityClearance', e.target.value)}>
            <option value="unknown">Not verified</option><option value="none">No FCL</option><option value="secret">Active Secret FCL</option><option value="top secret">Active Top Secret FCL</option>
          </Input>
          <Label>Staff currently available for new work</Label>
          <Input type="number" min="0" value={profile.availableStaff ?? ''} onChange={e => updateField('availableStaff', e.target.value === '' ? null : Number(e.target.value))} placeholder="Unknown — leave blank" />
          <Label>Preferred role</Label>
          <Input as="select" value={profile.preferredRole || 'either'} onChange={e => updateField('preferredRole', e.target.value)}>
            <option value="either">Prime or subcontractor, based on fit</option><option value="prime">Prefer prime</option><option value="subcontractor">Prefer subcontractor</option>
          </Input>
          <Label>Maximum prime contract value ($, company preference)</Label>
          <Input type="number" min="0" value={profile.maxPrimeContractValue ?? ''} onChange={e => updateField('maxPrimeContractValue', e.target.value === '' ? null : Number(e.target.value))} placeholder="Unknown — leave blank" />
          <Label>Operating limits, locations, and teaming requirements</Label>
          <Input as="textarea" rows="4" value={profile.operatingLimitations || ''} onChange={e => updateField('operatingLimitations', e.target.value)} placeholder="Document staffing, travel, delivery, financing, or subcontract constraints." />
          <label><input type="checkbox" checked={profile.corporateHistoryVerified === true} onChange={e => updateField('corporateHistoryVerified', e.target.checked)} /> I verified the listed corporate contracts were performed by this company, not a prior employer.</label>
        </FormSection>

        <FormSection>
          <Label>Contract Vehicles</Label>
          <ArrayInput>
            {profile.contractVehicles.map((item, i) => (
              <ArrayItem key={i}>
                <Input
                  type="text"
                  value={item}
                  onChange={(e) => updateArrayItem('contractVehicles', i, e.target.value)}
                  placeholder="e.g., GSA Schedule 70"
                  style={{ flex: 1 }}
                />
                <RemoveButton onClick={() => removeArrayItem('contractVehicles', i)}>
                  Remove
                </RemoveButton>
              </ArrayItem>
            ))}
            <AddButton type="button" onClick={() => addArrayItem('contractVehicles')}>
              + Add Contract Vehicle
            </AddButton>
          </ArrayInput>
        </FormSection>

        <FormSection>
          <Label>Key Differentiators</Label>
          <ArrayInput>
            {profile.differentiators.map((item, i) => (
              <ArrayItem key={i}>
                <TextArea
                  value={item}
                  onChange={(e) => updateArrayItem('differentiators', i, e.target.value)}
                  placeholder="What makes your company unique?"
                  style={{ flex: 1, minHeight: '60px' }}
                />
                <RemoveButton onClick={() => removeArrayItem('differentiators', i)}>
                  Remove
                </RemoveButton>
              </ArrayItem>
            ))}
            <AddButton type="button" onClick={() => addArrayItem('differentiators')}>
              + Add Differentiator
            </AddButton>
          </ArrayInput>
        </FormSection>

        <SaveButton type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save Profile'}
        </SaveButton>
      </Form>
    </Panel>
    </>
  );
}
